import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GitHubIcon from '@mui/icons-material/GitHub';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { BarChart } from '@mui/x-charts/BarChart';
import dayjs, { Dayjs } from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import './VelocityVisualiser.css';

type Member = {
  id: string;
  name: string;
  title?: string;
};

type Task = {
  id: string;
  memberId: string;
  title: string;
  startDate: string;
  endDate: string;
  points: number;
  effortHours: number;
  done: boolean;
};

/** memberId -> yyyy-mm-dd -> available hours that day */
type BandwidthMap = Record<string, Record<string, number>>;

type StoredState = {
  members: Member[];
  tasks: Task[];
  bandwidth: BandwidthMap;
};

const STORAGE_KEY = 'velocity_visualiser:public';
const SHOW_WEEKENDS_KEY = 'velocity_visualiser:showWeekends';
const DEFAULT_DAILY_HOURS = 8;

const isoDay = (d: Dayjs) => d.format('YYYY-MM-DD');

const clampToDay = (d: Dayjs) => dayjs(d.format('YYYY-MM-DD'));

function overlapsDay(task: Task, day: Dayjs): boolean {
  const s = dayjs(task.startDate);
  const e = dayjs(task.endDate);
  const x = clampToDay(day);
  return (x.isAfter(s, 'day') || x.isSame(s, 'day')) && (x.isBefore(e, 'day') || x.isSame(e, 'day'));
}

function inclusiveSpanDays(task: Task): number {
  const s = dayjs(task.startDate);
  const e = dayjs(task.endDate);
  return Math.max(1, e.diff(s, 'day') + 1);
}

function getTaskEffortHours(t: Task): number {
  if (Number.isFinite(t.effortHours) && t.effortHours >= 0) return t.effortHours;
  return t.points;
}

/** Hours attributed to this calendar day for the task (spread evenly across span). */
function hoursForTaskOnDay(task: Task, day: Dayjs): number {
  if (!overlapsDay(task, day)) return 0;
  const span = inclusiveSpanDays(task);
  return getTaskEffortHours(task) / span;
}

function normalizeStoredState(raw: Partial<StoredState>): StoredState {
  const members = Array.isArray(raw.members) ? raw.members : [];
  const tasksRaw = Array.isArray(raw.tasks) ? raw.tasks : [];
  const tasks: Task[] = tasksRaw.map((t) => ({
    id: String((t as Task).id),
    memberId: String((t as Task).memberId),
    title: String((t as Task).title ?? ''),
    startDate: String((t as Task).startDate ?? ''),
    endDate: String((t as Task).endDate ?? ''),
    points: typeof (t as Task).points === 'number' ? (t as Task).points : 0,
    effortHours:
      typeof (t as Task).effortHours === 'number' && Number.isFinite((t as Task).effortHours)
        ? Math.max(0, (t as Task).effortHours)
        : typeof (t as Task).points === 'number'
          ? Math.max(0, (t as Task).points)
          : 0,
    done: Boolean((t as Task).done),
  }));
  const bw: BandwidthMap = {};
  const rawBw = (raw as Partial<StoredState>).bandwidth;
  if (rawBw && typeof rawBw === 'object') {
    for (const [mid, days] of Object.entries(rawBw)) {
      if (!days || typeof days !== 'object') continue;
      bw[mid] = {};
      for (const [date, hrs] of Object.entries(days)) {
        if (typeof hrs === 'number' && Number.isFinite(hrs) && hrs >= 0) bw[mid][date] = hrs;
      }
    }
  }
  return { members, tasks, bandwidth: bw };
}

function useLocalStorageState(
  key: string,
  initialValue: StoredState
): [StoredState, React.Dispatch<React.SetStateAction<StoredState>>] {
  const [state, setState] = React.useState<StoredState>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return initialValue;
      const parsed = JSON.parse(raw) as Partial<StoredState>;
      return normalizeStoredState(parsed);
    } catch {
      return initialValue;
    }
  });

  const setAndPersist = React.useCallback(
    (next: StoredState | ((prev: StoredState) => StoredState)) => {
      setState((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: StoredState) => StoredState)(prev) : next;
        try {
          localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          /* ignore */
        }
        return resolved;
      });
    },
    [key]
  );

  return [state, setAndPersist];
}

function startOfWeek(d: Dayjs): Dayjs {
  const day = d.day();
  const diff = (day + 6) % 7;
  return clampToDay(d.subtract(diff, 'day'));
}

function weekDays(weekStart: Dayjs): Dayjs[] {
  return Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));
}

function isOverallocatedHours(used: number, available: number): boolean {
  return used > available + 0.01;
}

export default function VelocityVisualiser(): React.ReactElement {
  const theme = useTheme();

  const [stored, setStored] = useLocalStorageState(STORAGE_KEY, {
    members: [],
    tasks: [],
    bandwidth: {},
  });

  const [tab, setTab] = React.useState(0);
  const [selectedWeekStart, setSelectedWeekStart] = React.useState<Dayjs>(() => startOfWeek(dayjs()));
  const days = React.useMemo(() => weekDays(selectedWeekStart), [selectedWeekStart]);

  const [showWeekends, setShowWeekends] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_WEEKENDS_KEY) === '1';
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(SHOW_WEEKENDS_KEY, showWeekends ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [showWeekends]);

  /** Columns shown in calendar & PDF (Mon–Fri by default). */
  const visibleDays = React.useMemo(() => {
    if (showWeekends) return days;
    return days.filter((d) => {
      const dow = d.day();
      return dow !== 0 && dow !== 6;
    });
  }, [days, showWeekends]);

  const [memberName, setMemberName] = React.useState('');
  const [memberTitle, setMemberTitle] = React.useState('');

  const [taskMemberId, setTaskMemberId] = React.useState<string>('');
  const [taskTitle, setTaskTitle] = React.useState('');
  const [taskStart, setTaskStart] = React.useState<Dayjs | null>(dayjs());
  const [taskEnd, setTaskEnd] = React.useState<Dayjs | null>(dayjs().add(2, 'day'));
  const [taskPoints, setTaskPoints] = React.useState<number>(3);
  const [taskEffortHours, setTaskEffortHours] = React.useState<number>(3);
  const [taskDone, setTaskDone] = React.useState(false);

  React.useEffect(() => {
    if (stored.members.length === 0) {
      setTaskMemberId('');
      return;
    }
    if (!taskMemberId || !stored.members.some((m) => m.id === taskMemberId)) {
      setTaskMemberId(stored.members[0].id);
    }
  }, [stored.members, taskMemberId]);

  const getAvailableHours = React.useCallback(
    (memberId: string, day: Dayjs): number => {
      const key = isoDay(day);
      const v = stored.bandwidth[memberId]?.[key];
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v;
      return DEFAULT_DAILY_HOURS;
    },
    [stored.bandwidth]
  );

  const setBandwidthForCell = (memberId: string, day: Dayjs, hours: number) => {
    const dk = isoDay(day);
    const h = Math.max(0, Math.min(24, hours));
    setStored((prev) => {
      const nextBw: BandwidthMap = { ...prev.bandwidth, [memberId]: { ...prev.bandwidth[memberId] } };
      nextBw[memberId] = { ...nextBw[memberId], [dk]: h };
      return { ...prev, bandwidth: nextBw };
    });
  };

  const addMember = () => {
    const name = memberName.trim();
    if (!name) return;
    const id = `m_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    setStored((prev) => ({
      ...prev,
      members: [...prev.members, { id, name, title: memberTitle.trim() || undefined }],
    }));
    setMemberName('');
    setMemberTitle('');
    if (!taskMemberId) setTaskMemberId(id);
  };

  const removeMember = (memberId: string) => {
    setStored((prev) => {
      const { [memberId]: _, ...restBw } = prev.bandwidth;
      return {
        members: prev.members.filter((m) => m.id !== memberId),
        tasks: prev.tasks.filter((t) => t.memberId !== memberId),
        bandwidth: restBw,
      };
    });
  };

  const addTask = () => {
    if (!taskMemberId) return;
    const title = taskTitle.trim();
    if (!title) return;
    if (!taskStart || !taskEnd) return;
    const s = clampToDay(taskStart);
    const e = clampToDay(taskEnd);
    if (e.isBefore(s, 'day')) return;
    const id = `t_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const pts = Number.isFinite(taskPoints) ? Math.max(0, Math.round(taskPoints)) : 0;
    const eff = Number.isFinite(taskEffortHours) ? Math.max(0, taskEffortHours) : pts;
    setStored((prev) => ({
      ...prev,
      tasks: [
        ...prev.tasks,
        {
          id,
          memberId: taskMemberId,
          title,
          startDate: isoDay(s),
          endDate: isoDay(e),
          points: pts,
          effortHours: eff,
          done: taskDone,
        },
      ],
    }));
    setTaskTitle('');
    setTaskDone(false);
  };

  const removeTask = (taskId: string) => {
    setStored((prev) => ({ ...prev, tasks: prev.tasks.filter((t) => t.id !== taskId) }));
  };

  const handleClearAllVelocityTasks = () => {
    if (stored.tasks.length === 0) return;
    if (!window.confirm('Remove all tasks?')) return;
    setStored((prev) => ({ ...prev, tasks: [] }));
  };

  const handleClearAllVelocityData = () => {
    if (stored.members.length === 0 && stored.tasks.length === 0) return;
    if (!window.confirm('Remove all team members, tasks, and saved bandwidth?')) return;
    setStored({ members: [], tasks: [], bandwidth: {} });
  };

  const toggleDone = (taskId: string) => {
    setStored((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) => (t.id === taskId ? { ...t, done: !t.done } : t)),
    }));
  };

  const tasksForWeek = React.useMemo(() => {
    const weekStart = days[0];
    const weekEnd = days[6];
    return stored.tasks.filter((t) => {
      const s = dayjs(t.startDate);
      const e = dayjs(t.endDate);
      return !(e.isBefore(weekStart, 'day') || s.isAfter(weekEnd, 'day'));
    });
  }, [days, stored.tasks]);

  const usedHoursForCell = React.useCallback(
    (memberId: string, day: Dayjs): number => {
      let sum = 0;
      for (const t of stored.tasks) {
        if (t.memberId !== memberId || t.done) continue;
        sum += hoursForTaskOnDay(t, day);
      }
      return sum;
    },
    [stored.tasks]
  );

  const pointsByMember = React.useMemo(() => {
    const map = new Map<string, { planned: number; done: number }>();
    for (const m of stored.members) map.set(m.id, { planned: 0, done: 0 });
    for (const t of tasksForWeek) {
      const cur = map.get(t.memberId) ?? { planned: 0, done: 0 };
      cur.planned += t.points;
      if (t.done) cur.done += t.points;
      map.set(t.memberId, cur);
    }
    return map;
  }, [stored.members, tasksForWeek]);

  const memberColors = React.useMemo(() => {
    const base = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.info.main,
      theme.palette.success.main,
      theme.palette.warning.main,
    ];
    const colors = new Map<string, string>();
    stored.members.forEach((m, i) => colors.set(m.id, base[i % base.length]));
    return colors;
  }, [
    stored.members,
    theme.palette.info.main,
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
  ]);

  const exportWeekPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pdfDays = visibleDays;
    const w0 = pdfDays[0]?.format('MMM D') ?? '';
    const w1 = pdfDays[pdfDays.length - 1]?.format('MMM D, YYYY') ?? '';
    doc.setFontSize(16);
    doc.text('Release Pilot — Velocity Visualiser', 14, 14);
    doc.setFontSize(11);
    doc.text(`Week: ${w0} – ${w1}`, 14, 22);
    doc.setFontSize(9);
    doc.text(`Generated ${new Date().toLocaleString()}`, 14, 28);
    doc.setFontSize(8);
    doc.text(`Default daily bandwidth: ${DEFAULT_DAILY_HOURS} h (editable per person per day in app).`, 14, 34);
    if (!showWeekends) {
      doc.text('Saturday & Sunday columns hidden (weekdays only).', 14, 39);
    }

    const dateLabels = pdfDays.map((d) => `${d.format('ddd')}\n${d.format('MMM D')}`);
    const startY = showWeekends ? 38 : 43;

    const body: string[][] = [];
    for (const m of stored.members) {
      const row: string[] = [m.name];
      for (const d of pdfDays) {
        const avail = getAvailableHours(m.id, d).toFixed(1);
        const used = usedHoursForCell(m.id, d).toFixed(1);
        const memberTasks = stored.tasks.filter((t) => t.memberId === m.id);
        const cellTasks = memberTasks.filter((t) => overlapsDay(t, d));
        const lines: string[] = [`Avail ${avail} h / Used ${used} h`];
        for (const t of cellTasks) {
          const h = hoursForTaskOnDay(t, d);
          lines.push(`• ${t.title} (${h.toFixed(2)} h)${t.done ? ' ✓' : ''}`);
        }
        row.push(lines.join('\n'));
      }
      body.push(row);
    }

    autoTable(doc, {
      head: [['Team member', ...dateLabels]],
      body,
      startY,
      styles: { fontSize: 7, cellPadding: 2, valign: 'top', overflow: 'linebreak' },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { cellWidth: 38 },
      },
      margin: { left: 14, right: 14 },
    });

    doc.save(`velocity-week-${days[0].format('YYYY-MM-DD')}.pdf`);
  };

  const taskTooltipContent = (t: Task, day: Dayjs, hrsThisDay: number) => (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {t.title}
      </Typography>
      <Typography variant="caption" component="div" sx={{ display: 'block', mt: 0.5 }}>
        Assignee: {stored.members.find((x) => x.id === t.memberId)?.name ?? '—'}
      </Typography>
      <Typography variant="caption" component="div">
        Dates: {t.startDate} → {t.endDate}
      </Typography>
      <Typography variant="caption" component="div">
        Total effort: {getTaskEffortHours(t)} h • Points: {t.points} • This day: {hrsThisDay.toFixed(2)} h
      </Typography>
      <Typography variant="caption" component="div">
        Span: {inclusiveSpanDays(t)} day(s), spread evenly • {t.done ? 'Done' : 'Planned'}
      </Typography>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        className="vv-root"
        sx={{
          minHeight: '100vh',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.background.default, 1)} 40%)`,
        }}
      >
        <IconButton
          href="https://github.com/release-pilot/release-pilot-public"
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
          sx={{ position: 'fixed', top: 8, right: 8, zIndex: 1100 }}
          aria-label="GitHub"
        >
          <GitHubIcon />
        </IconButton>

        <Box
          className="vv-brand"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            background: alpha(theme.palette.background.paper, 0.85),
            backdropFilter: 'blur(8px)',
          }}
        >
          <Box
            component="img"
            src="/assets/Single_mainlogo_tm.svg"
            alt="Release Pilot"
            sx={{ height: { xs: 28, sm: 32 }, display: 'block' }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              Velocity Visualiser
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Team load, bandwidth, and effort across the week — Release Pilot
            </Typography>
          </Box>
          {stored.members.length > 0 && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PictureAsPdfIcon />}
              onClick={exportWeekPdf}
              sx={{ flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Export PDF
            </Button>
          )}
        </Box>

        <Box sx={{ flex: 1, width: '100%', px: { xs: 1.5, md: 2 }, py: 2, minHeight: 0 }}>
          <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
            <CardContent sx={{ pb: 1.5, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                  <Tab label="Setup" />
                  <Tab label="Calendar view" />
                  <Tab label="Velocity" />
                </Tabs>
                {stored.members.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={exportWeekPdf}
                    sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                  >
                    PDF
                  </Button>
                )}
              </Stack>

              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {tab === 0 && (
                  <Stack spacing={2.5}>
                    {(stored.members.length > 0 || stored.tasks.length > 0) && (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: { sm: 1 } }}>
                          Danger zone
                        </Typography>
                        <Button color="error" variant="outlined" size="small" onClick={handleClearAllVelocityTasks} disabled={stored.tasks.length === 0}>
                          Clear all tasks
                        </Button>
                        <Button color="error" variant="outlined" size="small" onClick={handleClearAllVelocityData}>
                          Clear team &amp; all data
                        </Button>
                      </Stack>
                    )}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="stretch">
                      <Card variant="outlined" sx={{ flex: 1 }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            Team members
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                            <TextField
                              label="Name"
                              size="small"
                              value={memberName}
                              onChange={(e) => setMemberName(e.target.value)}
                              fullWidth
                            />
                            <TextField
                              label="Role (optional)"
                              size="small"
                              value={memberTitle}
                              onChange={(e) => setMemberTitle(e.target.value)}
                              fullWidth
                            />
                            <Button variant="contained" startIcon={<AddIcon />} onClick={addMember} sx={{ whiteSpace: 'nowrap' }}>
                              Add
                            </Button>
                          </Stack>

                          {stored.members.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Add at least one team member to start assigning tasks.
                            </Typography>
                          ) : (
                            <Stack spacing={1}>
                              {stored.members.map((m) => (
                                <Box
                                  key={m.id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.5,
                                    px: 1.25,
                                    py: 1,
                                    borderRadius: 1,
                                    border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                                    backgroundColor: alpha(memberColors.get(m.id) ?? theme.palette.primary.main, 0.06),
                                  }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                                      {m.name}
                                    </Typography>
                                    {m.title && (
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                        {m.title}
                                      </Typography>
                                    )}
                                  </Box>
                                  <IconButton size="small" onClick={() => removeMember(m.id)} aria-label={`Remove ${m.name}`}>
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </CardContent>
                      </Card>

                      <Card variant="outlined" sx={{ flex: 1 }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            Tasks
                          </Typography>

                          <Stack spacing={1.5}>
                            <FormControl size="small" fullWidth>
                              <InputLabel id="vv-member">Assignee</InputLabel>
                              <Select
                                labelId="vv-member"
                                label="Assignee"
                                value={taskMemberId}
                                onChange={(e) => setTaskMemberId(String(e.target.value))}
                                disabled={stored.members.length === 0}
                              >
                                {stored.members.map((m) => (
                                  <MenuItem key={m.id} value={m.id}>
                                    {m.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <TextField
                              label="Task"
                              size="small"
                              value={taskTitle}
                              onChange={(e) => setTaskTitle(e.target.value)}
                              fullWidth
                              placeholder="e.g. Implement release notes export"
                              disabled={stored.members.length === 0}
                            />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                              <DatePicker
                                label="Start date"
                                value={taskStart}
                                onChange={(v) => setTaskStart(v)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                disabled={stored.members.length === 0}
                              />
                              <DatePicker
                                label="End date"
                                value={taskEnd}
                                onChange={(v) => setTaskEnd(v)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                disabled={stored.members.length === 0}
                              />
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                              <TextField
                                label="Points"
                                size="small"
                                type="number"
                                value={taskPoints}
                                onChange={(e) => {
                                  const v = Number(e.target.value);
                                  setTaskPoints(v);
                                  setTaskEffortHours(Number.isFinite(v) ? v : 0);
                                }}
                                inputProps={{ min: 0, step: 1 }}
                                fullWidth
                                disabled={stored.members.length === 0}
                              />
                              <TextField
                                label="Effort (hours)"
                                size="small"
                                type="number"
                                value={taskEffortHours}
                                onChange={(e) => setTaskEffortHours(Number(e.target.value))}
                                inputProps={{ min: 0, step: 0.25 }}
                                fullWidth
                                helperText="Spread evenly across each day in range"
                                disabled={stored.members.length === 0}
                              />
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Done
                              </Typography>
                              <Switch checked={taskDone} onChange={(e) => setTaskDone(e.target.checked)} disabled={stored.members.length === 0} />
                            </Stack>

                            <Button variant="contained" startIcon={<AddIcon />} onClick={addTask} disabled={stored.members.length === 0}>
                              Add task
                            </Button>
                          </Stack>

                          <Divider sx={{ my: 2 }} />

                          {stored.tasks.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              No tasks yet.
                            </Typography>
                          ) : (
                            <Stack spacing={1}>
                              {stored.tasks
                                .slice()
                                .sort((a, b) => (a.startDate < b.startDate ? -1 : 1))
                                .map((t) => {
                                  const member = stored.members.find((m) => m.id === t.memberId);
                                  const color = memberColors.get(t.memberId) ?? theme.palette.primary.main;
                                  return (
                                    <Box
                                      key={t.id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1.5,
                                        px: 1.25,
                                        py: 1,
                                        borderRadius: 1,
                                        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                                      }}
                                    >
                                      <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                          <Chip
                                            size="small"
                                            label={member?.name ?? 'Unassigned'}
                                            sx={{ bgcolor: alpha(color, 0.12), color: theme.palette.text.primary }}
                                          />
                                          <Typography sx={{ fontWeight: 600 }}>{t.title}</Typography>
                                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {t.startDate} → {t.endDate} • {t.points} pts • {getTaskEffortHours(t)} h
                                          </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                          <Button size="small" onClick={() => toggleDone(t.id)} sx={{ textTransform: 'none' }}>
                                            {t.done ? 'Mark planned' : 'Mark done'}
                                          </Button>
                                        </Stack>
                                      </Box>
                                      <IconButton size="small" onClick={() => removeTask(t.id)} aria-label="Remove task">
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  );
                                })}
                            </Stack>
                          )}
                        </CardContent>
                      </Card>
                    </Stack>
                  </Stack>
                )}

                {tab === 1 && (
                  <Stack spacing={2} sx={{ height: '100%' }}>
                    <Box
                      className="vv-calendar-options"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.info.main, 0.45)}`,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        boxShadow: '0 1px 4px rgba(14, 165, 233, 0.12)',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.06, color: 'info.dark', display: 'block', mb: 1 }}>
                        CALENDAR COLUMNS
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch checked={showWeekends} onChange={(_, v) => setShowWeekends(v)} color="primary" size="medium" />
                        }
                        label={
                          showWeekends
                            ? 'Showing full week (Mon–Sun)'
                            : 'Weekdays only (Mon–Fri) — turn on to show Saturday & Sunday'
                        }
                        sx={{ alignItems: 'flex-start', m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.4 } }}
                      />
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                      <Box>
                        <Typography variant="h6">Week view</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {visibleDays[0]?.format('MMM D') ?? '—'} – {visibleDays[visibleDays.length - 1]?.format('MMM D, YYYY') ?? '—'} • Hover tasks for
                          full detail • Set available hours per cell
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton onClick={() => setSelectedWeekStart((w) => w.subtract(7, 'day'))} aria-label="Previous week">
                          <ChevronLeftIcon />
                        </IconButton>
                        <Button variant="outlined" onClick={() => setSelectedWeekStart(startOfWeek(dayjs()))} sx={{ textTransform: 'none' }}>
                          This week
                        </Button>
                        <IconButton onClick={() => setSelectedWeekStart((w) => w.add(7, 'day'))} aria-label="Next week">
                          <ChevronRightIcon />
                        </IconButton>
                      </Stack>
                    </Stack>

                    {stored.members.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Add members and tasks in “Setup” first.
                      </Typography>
                    ) : (
                      <Box
                        className="vv-calendar-wrap"
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          overflow: 'auto',
                          flex: 1,
                          maxHeight: { xs: '70vh', md: 'calc(100vh - 220px)' },
                          width: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: `minmax(180px, 220px) repeat(${visibleDays.length}, minmax(130px, 1fr))`,
                            minWidth: { xs: Math.max(640, 200 + visibleDays.length * 124), md: '100%' },
                            bgcolor: theme.palette.background.paper,
                          }}
                        >
                          <Box sx={{ p: 1.5, borderRight: `1px solid ${theme.palette.divider}`, position: 'sticky', left: 0, zIndex: 2, bgcolor: theme.palette.background.paper }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                              Team
                            </Typography>
                          </Box>
                          {visibleDays.map((d) => {
                            const isWe = d.day() === 0 || d.day() === 6;
                            return (
                              <Box
                                key={d.toString()}
                                sx={{
                                  p: 1.5,
                                  borderRight: `1px solid ${theme.palette.divider}`,
                                  bgcolor: isWe ? alpha(theme.palette.secondary.main, 0.07) : alpha(theme.palette.primary.main, 0.04),
                                }}
                              >
                                <Typography sx={{ fontWeight: 800, lineHeight: 1.15, fontSize: '1rem' }}>{d.format('ddd')}</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                  {d.format('MMM D')}
                                </Typography>
                              </Box>
                            );
                          })}

                          {stored.members.map((m) => {
                            const memberTasks = stored.tasks.filter((t) => t.memberId === m.id);
                            const memberWeekTasks = memberTasks.filter((t) => tasksForWeek.some((x) => x.id === t.id));
                            const color = memberColors.get(m.id) ?? theme.palette.primary.main;
                            return (
                              <React.Fragment key={m.id}>
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    borderRight: `1px solid ${theme.palette.divider}`,
                                    bgcolor: alpha(color, 0.07),
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1,
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 800, fontSize: '1rem' }} noWrap>
                                    {m.name}
                                  </Typography>
                                  {m.title && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                      {m.title}
                                    </Typography>
                                  )}
                                </Box>

                                {visibleDays.map((d) => {
                                  const cellTasks = memberWeekTasks.filter((t) => overlapsDay(t, d));
                                  const avail = getAvailableHours(m.id, d);
                                  const used = usedHoursForCell(m.id, d);
                                  const warn = isOverallocatedHours(used, avail);
                                  const isWe = d.day() === 0 || d.day() === 6;
                                  return (
                                    <Box
                                      key={`${m.id}_${d.toString()}`}
                                      sx={{
                                        p: 1,
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                        borderRight: `1px solid ${theme.palette.divider}`,
                                        minHeight: 140,
                                        bgcolor: warn
                                          ? alpha(theme.palette.warning.main, 0.08)
                                          : isWe
                                            ? alpha(theme.palette.secondary.main, 0.04)
                                            : alpha(theme.palette.background.paper, 1),
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.75,
                                      }}
                                    >
                                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                          Avail (h)
                                        </Typography>
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={avail}
                                          onChange={(e) => setBandwidthForCell(m.id, d, Number(e.target.value))}
                                          inputProps={{ min: 0, max: 24, step: 0.5 }}
                                          sx={{
                                            width: 72,
                                            '& .MuiInputBase-input': { py: 0.5, fontSize: 13 },
                                          }}
                                        />
                                        <Chip
                                          size="small"
                                          label={`Used ${used.toFixed(1)} / ${avail.toFixed(1)}`}
                                          color={warn ? 'warning' : used > 0 ? 'primary' : 'default'}
                                          variant={warn ? 'filled' : 'outlined'}
                                          sx={{ height: 24, fontSize: 11 }}
                                        />
                                      </Stack>

                                      <Box
                                        sx={{
                                          flex: 1,
                                          overflow: 'auto',
                                          maxHeight: 200,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: 0.5,
                                          pr: 0.25,
                                        }}
                                      >
                                        {cellTasks.map((t) => {
                                          const hrs = hoursForTaskOnDay(t, d);
                                          const short =
                                            t.title.length > 42 ? `${t.title.slice(0, 40)}…` : t.title;
                                          return (
                                            <Tooltip
                                              key={t.id}
                                              title={taskTooltipContent(t, d, hrs)}
                                              arrow
                                              placement="top"
                                              enterDelay={250}
                                              componentsProps={{
                                                tooltip: {
                                                  sx: {
                                                    maxWidth: 420,
                                                    bgcolor: 'grey.900',
                                                    '& .MuiTypography-root': { color: 'common.white' },
                                                  },
                                                },
                                              }}
                                            >
                                              <Box>
                                                <Chip
                                                  size="small"
                                                  label={`${short}${t.done ? ' ✓' : ''} • ${hrs.toFixed(1)}h`}
                                                  sx={{
                                                    justifyContent: 'flex-start',
                                                    height: 'auto',
                                                    py: 0.5,
                                                    '& .MuiChip-label': {
                                                      whiteSpace: 'normal',
                                                      textAlign: 'left',
                                                      lineHeight: 1.35,
                                                    },
                                                    bgcolor: alpha(color, t.done ? 0.12 : 0.2),
                                                    textDecoration: t.done ? 'line-through' : 'none',
                                                    border: `1px solid ${alpha(color, 0.35)}`,
                                                    width: '100%',
                                                  }}
                                                />
                                              </Box>
                                            </Tooltip>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Stack>
                )}

                {tab === 2 && (
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                      <Box>
                        <Typography variant="h6">Velocity summary</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Planned vs done points for the selected week.
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={exportWeekPdf}>
                          Export PDF
                        </Button>
                        <IconButton onClick={() => setSelectedWeekStart((w) => w.subtract(7, 'day'))} aria-label="Previous week">
                          <ChevronLeftIcon />
                        </IconButton>
                        <Button variant="outlined" onClick={() => setSelectedWeekStart(startOfWeek(dayjs()))} sx={{ textTransform: 'none' }}>
                          This week
                        </Button>
                        <IconButton onClick={() => setSelectedWeekStart((w) => w.add(7, 'day'))} aria-label="Next week">
                          <ChevronRightIcon />
                        </IconButton>
                      </Stack>
                    </Stack>

                    {stored.members.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Add members and tasks in “Setup” first.
                      </Typography>
                    ) : (
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ width: '100%', overflowX: 'auto' }}>
                            <BarChart
                              height={360}
                              xAxis={[
                                {
                                  scaleType: 'band',
                                  data: stored.members.map((m) => m.name),
                                },
                              ]}
                              series={[
                                {
                                  label: 'Planned points',
                                  data: stored.members.map((m) => pointsByMember.get(m.id)?.planned ?? 0),
                                  color: alpha(theme.palette.primary.main, 0.8),
                                },
                                {
                                  label: 'Done points',
                                  data: stored.members.map((m) => pointsByMember.get(m.id)?.done ?? 0),
                                  color: alpha(theme.palette.success.main, 0.85),
                                },
                              ]}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    )}
                  </Stack>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
